import { WithTranslation, withTranslation } from '@wix/wix-i18n-config';
import classNames from 'classnames';
import React, { FC, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { RouterProvider } from 'react-router';

import { Loader } from '../common/components/Loader';
import { useMessageSubscriber } from '../common/hooks/useMessageSubscriber';
import { useStartChatListener } from '../common/hooks/useStartChatListener';
import { selectStartChatError, selectStartChatLoading } from '../common/selectors/chat';
import { loadChatbotStartChat } from '../common/thunks/chatbotStartChat.thunk';
import { ErrorScreen } from '../error';
import { chatbotFullPageRouter } from '../routes/routers';
import { widgetSDK } from '../sdk/widgetSDK';
import { renderModel } from '../util/renderModel';
import { appDataHooks } from './App.dataHooks';
import s from './App.scss';

export const App: FC<WithTranslation> = () => {
  // listen to application state changes and start over chat if needed
  useStartChatListener();

  // subscribe to duplexer events
  useMessageSubscriber();

  const dispatch = useDispatch();
  const startChatLoading = useSelector(selectStartChatLoading);
  const hasStartChatError = useSelector(selectStartChatError);

  useEffect(() => {
    const load = async () => {
      const chatbotContext = await widgetSDK.getContext(renderModel.getUserId());
      dispatch(loadChatbotStartChat(chatbotContext));
    };

    load();
  }, [dispatch]);

  const rootComponent = useMemo(() => {
    if (startChatLoading) {
      return <Loader dataHook={appDataHooks.loader()} height="100%" verticalAlign="middle" width="100%" />;
    }

    return hasStartChatError ? <ErrorScreen /> : <RouterProvider router={chatbotFullPageRouter} />;
  }, [startChatLoading, hasStartChatError]);

  return (
    <div
      className={classNames(s.root, { [s.rootMissingWixHeader]: renderModel.isWixHeaderDisabled() })}
      data-hook={appDataHooks.appContainer({})}
    >
      {rootComponent}
    </div>
  );
};

export default withTranslation()(App);
